import { z } from 'zod';
/**
 * Cross-tool graph node vocabulary. Every node label is mirrored as a
 * Cypher label in `apps/functions/src/graph/vocabulary.ts`; the two
 * lists must stay in sync. Adding a label here is a schema change —
 * the migration runner picks it up automatically and creates the
 * `(orgId, externalId)` uniqueness constraint on next deploy.
 */
export const GRAPH_NODE_LABELS = [
    'Person',
    'Account',
    'Deal',
    'Channel',
    'Message',
    'Email',
    'Thread', // conversation thread — Gmail thread / Slack thread_ts
    'Rock',
    'Issue',
    'Commit',
    'PullRequest',
    'Charge',
    'Subscription',
];
export const GraphNodeLabel = z.enum(GRAPH_NODE_LABELS);
/**
 * Edge types. Directed; the convention is `(:From)-[:EDGE]->(:To)`.
 *
 *   - `WORKS_AT`: Person → Account (derived from email domain + CRM)
 *   - `OWNS_ROCK`: Person → Rock (from Rock.ownerUid)
 *   - `OWNS_DEAL`: Person → Deal (from Salesforce Opportunity.OwnerId)
 *   - `IN_CHANNEL`: Person → Channel
 *   - `MENTIONS`: Message | Email → Rock | Person | Account
 *   - `AUTHORED`: Person → Message | Email | Commit | PullRequest
 *   - `REVIEWED`: Person → PullRequest
 *   - `MERGED`: Person → PullRequest
 *   - `STAGE_TRANSITION`: Deal → Deal (predecessor-of, with `from`/`to`/`at` props)
 *   - `PAID_FOR`: Charge → Subscription | Account
 *   - `BELONGS_TO`: Channel → Account; PullRequest → Account
 *   - `BLOCKS`: Rock | Issue → Rock | Issue (from "blocked by @x" patterns)
 *   - `MENTORED_BY`: Person → Person
 *   - `IN_THREAD`: Email | Message → Thread (Gmail thread / Slack thread_ts conversations)
 *   - `RECEIVED_BY`: Email → Person (To / Cc / Bcc recipients on a Gmail message)
 */
export const GRAPH_EDGE_TYPES = [
    'WORKS_AT',
    'OWNS_ROCK',
    'OWNS_DEAL',
    'IN_CHANNEL',
    'MENTIONS',
    'AUTHORED',
    'REVIEWED',
    'MERGED',
    'STAGE_TRANSITION',
    'PAID_FOR',
    'BELONGS_TO',
    'BLOCKS',
    'MENTORED_BY',
    'IN_THREAD', // Email | Message → Thread (gmail thread / slack thread_ts)
    'RECEIVED_BY', // Email → Person (recipient: To / Cc / Bcc)
];
export const GraphEdgeType = z.enum(GRAPH_EDGE_TYPES);
/**
 * Confidence band for entity-resolution decisions.
 *
 *   - `high` (>0.95): auto-merge silently
 *   - `mid`  (0.70 ≤ score ≤ 0.95): suggest + queue for admin approval
 *   - `low`  (<0.70): keep separate; flag in admin entity-resolution UI
 *
 * Bands are computed by `bandForScore` in entity-resolution.ts.
 */
export const RESOLUTION_CONFIDENCE_BANDS = ['high', 'mid', 'low'];
export const ResolutionConfidenceBand = z.enum(RESOLUTION_CONFIDENCE_BANDS);
export const RESOLUTION_BAND_THRESHOLDS = {
    high: 0.95,
    mid: 0.7,
};
